import { useStaticLabels } from 'shared/hooks/use-static-labels'
import styled from 'styled-components'

import { SIZES } from '~/common/globalStyles'
import { ProjectType } from '~/common/types/graphql'
import BaseProgressBar from '~/views/components/ui/MultipleProgressBar'
import {
  ProgressBarLabel,
  ProgressBarLabelGrid,
} from '~/views/components/ui/ProgressBar/elements'

const ProgressBar = styled(BaseProgressBar)`
  margin-top: ${SIZES.small};
  cursor: pointer;
`

const progressBarValueFrom = (
  percentage: number,
  color: string,
  hoverText: string,
  status: string,
) => ({
  percentage: percentage,
  color: color,
  hoverText: hoverText,
  status: status,
})

interface Props {
  size: keyof typeof SIZES
  projectId: ProjectType['id']
  firstActiveTemplateId?: ProjectType['firstActiveTemplate'] | null
  disableClick: boolean
  projectStats: {
    totalTasks: number
    totalDoneTasks: number
    totalSignOffTasks: number
    totalStuckTasks: number
    totalWorkingOnItTasks: number
    totalNotStartedTasks: number
    totalAssignedTasks: number
    totalCustomerOverdueTasks: number
    totalThirdPartyOverdueTasks: number
    totalInternalOverdueTasks: number
  }
}

const ProjectProgress = ({
  projectStats,
  size = 'xxs',
  projectId,
  firstActiveTemplateId,
  disableClick,
}: Props) => {
  const [getCommonLabel] = useStaticLabels(['common'])

  const totalTasks = projectStats.totalTasks || 0
  const totalDoneTasks = projectStats.totalDoneTasks || 0
  const totalSignOffTasks = projectStats.totalSignOffTasks || 0
  const totalStuckTasks = projectStats.totalStuckTasks || 0
  const totalWorkingOnItTasks = projectStats.totalWorkingOnItTasks || 0
  const totalNotStartedTasks = projectStats.totalNotStartedTasks || 0
  const totalRemainingTasks = totalTasks - totalDoneTasks
  const percentTasksDoneRaw = totalDoneTasks / totalTasks
  const percentTasksSignOffRaw = totalSignOffTasks / totalTasks
  const percentTasksStuckRaw = totalStuckTasks / totalTasks
  const percentTasksWorkingOnItRaw = totalWorkingOnItTasks / totalTasks
  const percentTasksNotStartedRaw = totalNotStartedTasks / totalTasks

  const percentTasksDone = isNaN(percentTasksDoneRaw) ? 0 : percentTasksDoneRaw
  const percentTasksSignOff = isNaN(percentTasksSignOffRaw)
    ? 0
    : percentTasksSignOffRaw
  const percentTasksStuck = isNaN(percentTasksStuckRaw)
    ? 0
    : percentTasksStuckRaw
  const percentTasksWorkingOnIt = isNaN(percentTasksWorkingOnItRaw)
    ? 0
    : percentTasksWorkingOnItRaw
  const percentTasksNotStarted = isNaN(percentTasksNotStartedRaw)
    ? 0
    : percentTasksNotStartedRaw

  return (
    <div>
      <ProgressBarLabelGrid>
        <ProgressBarLabel
          label={getCommonLabel('completed')}
          color="green"
          total={totalDoneTasks}
          percentage={percentTasksDone}
          labels={{
            task: getCommonLabel('task'),
            tasks: getCommonLabel('tasks'),
          }}
        />
        <ProgressBarLabel
          label={getCommonLabel('remaining')}
          color="lighterGray"
          total={totalRemainingTasks}
          percentage={totalRemainingTasks / totalTasks}
          labels={{
            task: getCommonLabel('task'),
            tasks: getCommonLabel('tasks'),
          }}
        />
      </ProgressBarLabelGrid>
      <ProgressBar
        onClick={(e) => disableClick && e.preventDefault()}
        projectId={projectId}
        firstActiveTemplateId={firstActiveTemplateId}
        values={[
          progressBarValueFrom(
            percentTasksDone,
            'green',
            getCommonLabel('done'),
            'DONE',
          ),
          progressBarValueFrom(
            percentTasksSignOff,
            'purple',
            getCommonLabel('signOff'),
            'SIGN_OFF',
          ),
          progressBarValueFrom(
            percentTasksStuck,
            'red',
            getCommonLabel('stuck'),
            'STUCK',
          ),
          progressBarValueFrom(
            percentTasksWorkingOnIt,
            'blue',
            getCommonLabel('inProgress'),
            'WORKING_ON_IT',
          ),
          progressBarValueFrom(
            percentTasksNotStarted,
            'darkGray',
            getCommonLabel('notStarted'),
            'NOT_STARTED',
          ),
        ]}
        size={size}
      />
    </div>
  )
}

export default ProjectProgress
